import { useLocation, Link as ReactRouterLink } from 'react-router-dom';
import { Badge, Box, Button, Icon, IconButton, Tooltip, type ButtonProps } from '@chakra-ui/react';

import type { CSSProperties } from 'react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export interface NavButtonProps extends ButtonProps {
  expanded: boolean;
  icon: React.ElementType;
  iconStyles?: CSSProperties;
  label: string;
  link: string;
  badge?: number;
}

/**
 * `NavButton` is a component that renders a navigation button with an icon and label.
 * The button can be in an expanded or collapsed state.
 */
export function NavButton(props: NavButtonProps) {
  const { expanded, icon, iconStyles, label, link, badge, ...buttonProps } = props;
  const location = useLocation();
  const linkIsCurrentPath = link.length > 1 && location.pathname.startsWith(link);

  if (expanded) {
    return (
      <Button
        as={ReactRouterLink}
        to={link}
        variant="tertiary"
        justifyContent="start"
        {...buttonProps}
        bg={linkIsCurrentPath ? 'bg.emphasis' : undefined}
        w="full"
        px="2"
        leftIcon={<Icon as={icon} boxSize={iconSize.xl} color="fg.subtle" style={iconStyles} />}
        rightIcon={
          badge !== undefined && badge > 0 ? (
            <Badge
              bg="red.500"
              color="fg.inverse"
              borderRadius="full"
              fontSize="2xs"
              fontWeight="bold"
              w="5"
              h="5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px="0"
              lineHeight="5"
            >
              {badge}
            </Badge>
          ) : undefined
        }
      >
        {label}
      </Button>
    );
  } else {
    return (
      <Box position="relative">
        <Tooltip label={label} zIndex="sticky" display={expanded ? 'none' : 'inline-flex'}>
          <IconButton
            as={ReactRouterLink}
            to={link}
            variant="tertiary"
            aria-label={label}
            icon={<Icon as={icon} boxSize={iconSize.xl} color="fg.subtle" style={iconStyles} />}
            {...buttonProps}
            bg={linkIsCurrentPath ? 'bg.emphasis' : undefined}
          />
        </Tooltip>
        {badge !== undefined && badge > 0 && (
          <Badge
            position="absolute"
            top="-1"
            right="-1"
            bg="red.500"
            color="fg.inverse"
            borderRadius="full"
            fontSize="2xs"
            fontWeight="bold"
            w="5"
            h="5"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px="0"
            lineHeight="5"
          >
            {badge}
          </Badge>
        )}
      </Box>
    );
  }
}

export default NavButton;
